import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import { h } from "vue";
import { createVuetify, type IconProps, type IconSet, type ThemeDefinition } from "vuetify";
import { mdi } from "vuetify/iconsets/mdi";
import { aliases, md } from "vuetify/iconsets/md";
import type { AppBranding } from "@/app/theme";
import { buildTenantThemes } from "@/app/branding";

/**
 * Nav entries and home tiles (A7) name their icons as Google Material Icons
 * LIGATURES (`light_mode`, `chevron_right`, `logout`, …), not MDI slugs, and
 * this shell's own chrome uses the same convention. `defaultSet: "mdi"` alone
 * silently blanks every one of those — MDI expects a hyphenated `mdi-*`
 * class, so an underscored ligature name never matches a real glyph.
 * Composite set: route an explicit `mdi-*` name to the MDI webfont,
 * everything else to Material Icons — requires the Material Icons webfont
 * link in `index.html`.
 */
const shellIcons: IconSet = {
  component: (props: IconProps) =>
    h(typeof props.icon === "string" && props.icon.startsWith("mdi-") ? mdi.component : md.component, props),
};

/**
 * Built from the app's own branding (`src/app/`) rather than from a module's static theme: the shell
 * is the thing that PUBLISHES the colours a module overlays, so it has to have them first. It used to
 * be built after `GET /tenant-apps/resolve` answered, for the same reason; D118 removed the wait.
 */
export function createShellVuetify(branding: AppBranding | null) {
  const { light, dark } = buildTenantThemes(branding);
  const defaultTheme = branding?.defaultTheme === "light" ? "light" : "dark";

  return createVuetify({
    icons: { defaultSet: "shell", aliases, sets: { shell: shellIcons, mdi, md } },
    display: { mobileBreakpoint: 768 },
    theme: {
      defaultTheme,
      themes: {
        light: light as ThemeDefinition,
        dark: dark as ThemeDefinition,
      },
    },
    // House defaults, shared with the modules the shell hosts so a menu opened
    // from the shell chrome and a menu opened inside a hosted module are the
    // same object.
    defaults: {
      global: { ripple: true },
      VTextField: { variant: "outlined", density: "compact", hideDetails: "auto", color: "primary" },
      VSelect: { variant: "outlined", density: "compact", hideDetails: "auto", color: "primary" },
      VBtn: { rounded: "lg", flat: true, class: "text-none", style: "text-transform:none!important;letter-spacing:0.01em!important" },
      VCard: { rounded: "lg", color: "card" },
      VList: { density: "compact", bgColor: "card" },
      VChip: { rounded: "pill", size: "small" },
      VMenu: { transition: "fade-transition" },
    },
  });
}
